import { createSignal, For, Match, onMount, Show, Switch } from "solid-js";
import { OrderDetails, VoucherDetails } from "../../server/types/order";
import { getHowToRedeemDetails } from "../../data/products";
import { getMarkdown } from "../../data/static";
import { HubbleRoute, pushPage } from "../../shared_states/modal";
import { showSnackBar } from "../../shared_states/snackbar";
import { classNames, getHexColorBrightness } from "../../utils/etc";
import { toRupees } from "../../utils/number";
import { PhosphorIcon } from "../../widgets/icons";
import { ResourceLoader } from "../../widgets/resource_loader";
import { UseOnBrandButton } from "../../widgets/use_on_brand_button";
import { VoucherCardPlain } from "../voucher_edit/voucher_card";
import { HowToUseSteps } from "~/server/types/brand";
import { useClassicContext } from "~/data/classic_context";
import { fetchV4Home } from "~/data/auth";
import SolidMarkdown from "solid-markdown";
import { Spacer } from "~/widgets/spacer";
import { SwitchButton } from "~/widgets/button";
import { clientRepo } from "~/server/apis/client_repo";
import { copyText } from "~/utils/common";
import HubbleImage from "~/widgets/hubble_image";
import { DonationDetails, getDonationDetails } from "~/data/donation";
import { config } from "~/data/config";

export function VoucherGenSuccess(props: {
  orderId: string;
  orderDetails?: OrderDetails;
  transactionId?: string;
  onClose: () => void;
  refreshProfile: boolean;
  donationDetails?: DonationDetails;
}) {
  const { data, isClientDonationType, setData } = useClassicContext();

  const v4Home = data.isLoggedIn ? data.v4HomePage : null;

  const clientName = data.isLoggedIn && data.clientConfig.partnerName;

  onMount(async () => {
    if (props.refreshProfile) {
      const v4HomePage = await fetchV4Home();

      // @ts-ignore
      setData("v4HomePage", (_) => v4HomePage);
    }
  });

  const getOrderDetail = async () => {
    const orderDetail =
      props.orderDetails ??
      (await clientRepo.getStoreOrderDetails(props.orderId));

    if (isClientDonationType() && props.transactionId) {
      const donationDetails = await getDonationDetails(props.transactionId);
      return {
        orderDetail,
        donationDetails,
      };
    }
    return {
      orderDetail,
    };
  };

  return (
    <ResourceLoader fetch={async () => getOrderDetail()}>
      {(response) => {
        const orderDetail = response.orderDetail;
        return (
          <div
            class={`no-scrollbar absolute bottom-0 left-0 right-0 top-0 box-border flex flex-col items-center justify-items-center overflow-x-hidden overflow-y-scroll bg-white pb-10`}
          >
            <PhosphorIcon
              name="x"
              class="fixed left-4 top-4 z-[101] cursor-pointer rounded-3xl border border-baseTertiaryDark bg-white p-1.5"
              onClick={props.onClose}
            />
            <Show
              when={
                !isClientDonationType() &&
                !(response.donationDetails || props.donationDetails)
              }
              fallback={
                <div
                  class="absolute left-0 right-0 top-0 h-[285px] w-full rounded-b-2xl bg-green-100"
                  style={{
                    background:
                      "linear-gradient(180.22deg, #D7FFEA 61.69%, #E6FBD2 99.81%)",
                  }}
                />
              }
            >
              <div class="absolute -top-[150px] left-0 right-0 flex h-[316px] flex-col items-center overflow-visible">
                <Checker />

                <div class="h-[316px] w-[396px] flex-shrink-0 rounded-[396px] bg-[#1ca9604d] blur-[75px] filter"></div>
              </div>
            </Show>
            <div class="relative z-[100] box-border flex flex-col items-stretch p-3">
              <SuccessIcon />
              <Switch>
                <Match when={isClientDonationType()}>
                  <div class="mb-7 w-full flex-col items-center justify-center gap-2 text-center">
                    <div class="text-bold font-bold text-successDark">
                      Thank you
                    </div>
                    <div class="text-h2 font-black text-successDark">
                      {toRupees(orderDetail.orderSavings.totalCoinEarnings)}{" "}
                      DONATED !
                    </div>
                    <Show
                      when={response.donationDetails || props.donationDetails}
                    >
                      <div
                        class="mt-3 flex items-start justify-between rounded-[12px] bg-white p-3"
                        style={{
                          border: "1px solid rgba(237, 238, 241, 1)",
                        }}
                      >
                        <div class="flex flex-col items-start justify-start">
                          <div class="mb-1 text-f12Bold text-successDark">
                            Donated towards
                          </div>
                          <div class="text-left text-bold">
                            {response.donationDetails?.name ??
                              props.donationDetails?.name}
                          </div>
                        </div>
                        <div class="h-16 w-[100px] rounded-2xl">
                          <HubbleImage
                            src={
                              response.donationDetails?.imageUrl ??
                              props.donationDetails?.imageUrl ??
                              ""
                            }
                            alt=""
                            class="h-16 w-[100px] rounded-2xl"
                          />
                        </div>
                      </div>
                    </Show>
                  </div>
                </Match>
                <Match when={true}>
                  <span
                    class="title-section self-center text-center text-mediumBold text-successDark [text-shadow:0px_0px_12px_rgba(0,_0,_0,_0.05)]"
                    classList={{
                      lowercase:
                        config.customizedSDKClients.lowercaseTitleText.includes(
                          clientName || ""
                        ),
                      uppercase:
                        !config.customizedSDKClients.lowercaseTitleText.includes(
                          clientName || ""
                        ),
                    }}
                  >
                    voucher worth {toRupees(orderDetail.amount)}{" "}
                  </span>
                  <span
                    class="title-display self-center  text-center text-h2 font-black text-successDark [text-shadow:0px_0px_12px_rgba(0,_0,_0,_0.05)]"
                    classList={{
                      "normal-case":
                        config.customizedSDKClients.lowercaseTitleText.includes(
                          clientName || ""
                        ),
                      uppercase:
                        !config.customizedSDKClients.lowercaseTitleText.includes(
                          clientName || ""
                        ),
                    }}
                  >
                    Ready to use!
                  </span>
                  <Show when={v4Home && v4Home.shop.savedTillDate > 0}>
                    <span class="title-secondary flex items-center gap-1.5 self-center whitespace-nowrap text-center text-mediumSemiBold text-successDark">
                      <SavingsArrow />
                      {toRupees(v4Home!.shop.savedTillDate)} saved till date
                      <SavingsArrowRtl />
                    </span>
                  </Show>
                </Match>
              </Switch>
              <For each={orderDetail.orderProductDetails.vouchers}>
                {(voucher) => {
                  return <VoucherCardWrap voucher={voucher} />;
                }}
              </For>
              <Show
                when={
                  orderDetail.orderProductDetails.vouchers?.[0]?.voucherMetadata
                    ?.howToUseDetailedStepsAvailable ||
                  orderDetail.orderProductDetails.vouchers?.[0]?.voucherMetadata
                    ?.tncResId !== ""
                }
              >
                <Spacer height={12} />
                <div class="box-border flex flex-col items-stretch rounded-[28px] border border-baseTertiaryDark bg-baseTertiaryLight px-5 py-0 [box-shadow:0px_3px_8px_0px_rgba(0,_0,_0,_0.04)]">
                  <Show
                    when={
                      orderDetail.orderProductDetails.vouchers?.[0]
                        ?.voucherMetadata?.howToUseDetailedStepsAvailable
                    }
                  >
                    <div
                      class="flex cursor-pointer gap-1.5 border-t border-baseTertiaryMedium px-0 py-4 first:border-t-0"
                      onClick={() => {
                        pushPage(
                          () => {
                            return (
                              <HowToUse
                                productId={
                                  orderDetail.orderProductDetails.productId
                                }
                              />
                            );
                          },
                          { isModal: true },
                          HubbleRoute.HOW_TO_USE
                        );
                      }}
                    >
                      <PhosphorIcon name="question" />
                      <span class="button-secondary flex items-center justify-between text-[13px]">
                        How to use?
                      </span>
                    </div>
                  </Show>
                  <Show
                    when={
                      orderDetail.orderProductDetails.vouchers?.[0]
                        ?.voucherMetadata?.tncResId !== ""
                    }
                  >
                    <div
                      class="flex cursor-pointer gap-1.5 border-t border-baseTertiaryMedium px-0 py-4 first:border-t-0"
                      onClick={() => {
                        pushPage(
                          () => {
                            return (
                              <FullTnc
                                resId={
                                  orderDetail.orderProductDetails.vouchers?.[0]
                                    ?.voucherMetadata?.tncResId ?? ""
                                }
                              />
                            );
                          },
                          { isModal: true },
                          HubbleRoute.TNC
                        );
                      }}
                    >
                      <PhosphorIcon name="notepad" />
                      <span class="button-secondary flex items-center justify-between text-[13px]">
                        Terms and conditions
                      </span>
                    </div>
                  </Show>
                </div>
              </Show>
            </div>
          </div>
        );
      }}
    </ResourceLoader>
  );
}

function VoucherCardWrap(props: { voucher: VoucherDetails }) {
  const cardProps: {
    label: string;
    value: string;
    display: string;
  }[] = [];

  switch (props.voucher.voucherMetadata.voucherRedemptionType) {
    case "CARD_AND_PIN_NO_SECURED":
      cardProps.push({
        label: "Card number",
        value: props.voucher.cardNumber,
        display: props.voucher.cardNumber,
      });
      cardProps.push({
        label: "Pin",
        value: props.voucher.cardPin,
        display: props.voucher.cardPin,
      });
      break;

    case "CARD_NUMBER_SECURED":
      cardProps.push({
        label: "Card number",
        value: props.voucher.cardNumber,
        display: props.voucher.cardNumber,
      });
      break;
    case "PIN_NO_SECURED":
      cardProps.push({
        label: "Pin",
        value: props.voucher.cardPin,
        display: props.voucher.cardPin,
      });
  }

  const brightness = getHexColorBrightness(
    props.voucher.voucherMetadata.cardBackgroundColor
  );

  const [isVoucherUsed, setIsVoucherUsed] = createSignal<boolean>(
    ["EXHAUSTED", "EXPIRED", "CANCELED"].includes(props.voucher.status)
  );

  const onVoucherMarkedAsUsedToggle = async (isUsed: boolean) => {
    setIsVoucherUsed(isUsed);

    try {
      await clientRepo.toggleVoucherStatus(props.voucher.id);
    } catch (e) {
      showSnackBar({
        level: "error",
        message: "Something went wrong. Please try later.",
      });

      setIsVoucherUsed(!isUsed);
    }
  };

  return (
    <div class="mt-[16px] box-border flex max-w-[375px] flex-col items-stretch gap-3 rounded-[28px] border border-baseTertiaryDark bg-baseTertiaryLight p-3 [box-shadow:0px_3px_8px_0px_rgba(0,_0,_0,_0.04)] max-[375px]:self-stretch min-[376px]:self-center">
      <VoucherCardPlain
        backgroundColor={props.voucher.voucherMetadata.cardBackgroundColor}
        displayText={"Card worth"}
        plainLogoUrl={props.voucher.voucherMetadata.plainLogoUrl!}
        value={toRupees(props.voucher.amount)}
        class="min-[376px]:min-w-[335px]"
      />

      <For each={cardProps}>
        {(props) => {
          return <CardProperty {...props} />;
        }}
      </For>

      <Show when={props.voucher.voucherMetadata.addVoucherBrandPageUrl}>
        <UseOnBrandButton
          brandName={props.voucher.voucherMetadata.title!}
          deeplink={props.voucher.voucherMetadata.addVoucherBrandPageUrl!}
          copyDataToClipboard={async () => {
            if (
              props.voucher.voucherMetadata.voucherRedemptionType ===
              "PIN_NO_SECURED"
            ) {
              await copyText({
                text: props.voucher.cardPin,
                messageOnSuccess: "Copied pin to clipboard",
                messageOnError: "Failed to copy pin",
              });
            } else {
              await copyText({
                text: props.voucher.cardNumber,
                messageOnSuccess: "Copied card no. to clipboard",
                messageOnError: "Failed to copy card no.",
              });
            }
            await new Promise((resolve) => setTimeout(resolve, 500));
          }}
        />
      </Show>

      <div class="flex flex-row items-center self-center">
        <span class="title-secondary text-f12Bold text-textNormal">
          Mark as used &nbsp;
        </span>
        <SwitchButton
          value={isVoucherUsed()}
          onChange={onVoucherMarkedAsUsedToggle}
          isSmall={true}
        />
      </div>
    </div>
  );
}

function CardProperty(props: {
  label: string;
  value: string;
  display: string;
}) {
  return (
    <div class="flex flex-row justify-between p-1.5">
      <div class="flex flex-col">
        <span class="body-tiny text-f12 text-baseSecondaryMedium">
          {props.label}
        </span>
        <span class="title-secondary text-mediumBold text-textDark">
          {props.display}
        </span>
      </div>
      <div
        class="button-secondary flex items-center gap-1.5 text-center text-mediumSemiBold text-basePrimaryDark"
        onClick={async () => {
          await copyText({ text: props.value });
        }}
      >
        Copy
        <PhosphorIcon name="copy-simple" class="text-[20px]" size="bold" />
      </div>
    </div>
  );
}

function Checker() {
  return (
    <svg
      class="absolute z-[3] h-[316px] w-[396px]"
      xmlns="http://www.w3.org/2000/svg"
      width="375"
      height="346"
      viewBox="0 0 375 346"
      fill="#ffffff7a"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M23.3165 261.593L23.5386 228.518L-32.9874 228.13L-32.9869 227.13L23.5453 227.518L23.7674 194.441L-32.9739 194.051L-32.9735 193.051L23.7741 193.441L23.9961 160.366L-32.9618 159.974L-32.9614 158.974L24.0028 159.366L24.2249 126.289L-32.9489 125.897L-32.9485 124.897L24.2316 125.289L24.4537 92.2129L-32.9358 91.8188L-32.9353 90.8188L24.4604 91.213L24.6824 58.1373L-32.92 57.7418L-32.9195 56.7418L24.6892 57.1374L24.9112 24.0609L-32.9076 23.6639L-32.9071 22.6639L24.9179 23.0609L25.14 -10.0153L-32.8947 -10.4138L-32.8942 -11.4138L25.1467 -11.0153L25.6981 -93.1437L26.7008 -93.1432L26.1493 -11.0084L57.9437 -10.7901L58.4965 -93.1312L59.4992 -93.1307L58.9463 -10.7832L90.7426 -10.5649L91.2968 -93.118L92.2995 -93.1176L91.7452 -10.558L123.54 -10.3397L124.095 -93.1058L125.098 -93.1053L124.542 -10.3328L156.339 -10.1144L156.896 -93.0924L157.899 -93.0919L157.342 -10.1075L189.135 -9.88923L189.694 -93.0792L190.697 -93.0787L190.138 -9.88235L221.934 -9.66401L222.494 -93.0652L223.497 -93.0647L222.937 -9.65713L254.732 -9.4388L255.293 -93.0518L256.296 -93.0513L255.734 -9.43192L287.53 -9.21358L288.093 -93.0397L289.096 -93.0393L288.533 -9.2067L320.328 -8.98838L320.892 -93.0264L321.894 -93.0259L321.33 -8.9815L353.127 -8.76316L353.692 -93.0128L354.695 -93.0123L354.129 -8.75628L412.187 -8.35761L412.187 -7.35761L354.123 -7.75632L353.901 25.3199L412.174 25.7201L412.174 26.7201L353.894 26.3199L353.672 59.3964L412.162 59.798L412.162 60.798L353.665 60.3963L353.443 93.472L412.146 93.875L412.146 94.875L353.436 94.4719L353.214 127.548L412.133 127.953L412.133 128.953L353.208 128.548L352.986 161.625L412.12 162.031L412.12 163.031L352.979 162.625L352.757 195.7L412.108 196.108L412.108 197.108L352.75 196.7L352.528 229.777L412.095 230.186L412.094 231.186L352.521 230.777L352.299 263.852L412.082 264.262L412.082 265.262L352.292 264.852L351.753 345.146L350.751 345.146L351.29 264.845L319.493 264.626L318.953 345.133L317.95 345.132L318.491 264.62L286.696 264.401L286.154 345.119L285.151 345.119L285.693 264.394L253.897 264.176L253.354 345.107L252.351 345.107L252.895 264.169L221.1 263.951L220.555 345.094L219.553 345.093L220.098 263.944L188.301 263.726L187.755 345.08L186.752 345.079L187.299 263.719L155.505 263.5L154.957 345.067L153.954 345.066L154.502 263.493L122.705 263.275L122.156 345.053L121.154 345.053L121.703 263.268L89.9084 263.05L89.3579 345.041L88.3552 345.041L88.9057 263.043L57.1094 262.825L56.5575 345.028L55.5549 345.027L56.1068 262.818L24.3124 262.6L23.7591 345.015L22.7565 345.015L23.3098 262.593L-33 262.206L-32.9995 261.206L23.3165 261.593ZM353.12 -7.76321L321.323 -7.98154L321.101 25.0947L352.898 25.3131L353.12 -7.76321ZM320.321 -7.98843L288.526 -8.20674L288.304 24.8695L320.099 25.0878L320.321 -7.98843ZM287.524 -8.21363L255.728 -8.43196L255.505 24.6443L287.302 24.8626L287.524 -8.21363ZM254.725 -8.43884L222.93 -8.65717L222.708 24.4191L254.503 24.6374L254.725 -8.43884ZM221.928 -8.66405L190.131 -8.88239L189.909 24.1939L221.706 24.4122L221.928 -8.66405ZM189.129 -8.88927L157.335 -9.10759L157.113 23.9687L188.907 24.187L189.129 -8.88927ZM156.332 -9.11447L124.535 -9.33281L124.313 23.7435L156.11 23.9618L156.332 -9.11447ZM123.533 -9.33969L91.7385 -9.55801L91.5165 23.5182L123.311 23.7366L123.533 -9.33969ZM90.7359 -9.5649L58.9396 -9.78323L58.7175 23.293L90.5138 23.5114L90.7359 -9.5649ZM57.937 -9.79012L26.1426 -10.0084L25.9206 23.0678L57.7149 23.2861L57.937 -9.79012ZM288.297 25.8695L320.092 26.0878L319.87 59.1643L288.075 58.946L288.297 25.8695ZM288.069 59.9459L319.863 60.1642L319.641 93.2399L287.847 93.0215L288.069 59.9459ZM320.866 60.1711L320.644 93.2467L352.44 93.4651L352.662 60.3895L320.866 60.1711ZM287.84 94.0215L319.635 94.2398L319.412 127.316L287.618 127.098L287.84 94.0215ZM320.637 94.2467L320.415 127.323L352.212 127.541L352.434 94.465L320.637 94.2467ZM287.611 128.098L319.406 128.316L319.184 161.393L287.389 161.174L287.611 128.098ZM320.408 128.323L320.186 161.399L351.983 161.618L352.205 128.541L320.408 128.323ZM287.382 162.174L319.177 162.392L318.955 195.468L287.16 195.25L287.382 162.174ZM320.18 162.399L319.958 195.475L351.754 195.693L351.976 162.618L320.18 162.399ZM287.154 196.25L318.948 196.468L318.726 229.545L286.932 229.327L287.154 196.25ZM319.951 196.475L319.729 229.552L351.525 229.77L351.747 196.693L319.951 196.475ZM286.925 230.327L318.719 230.545L318.497 263.62L286.703 263.401L286.925 230.327ZM319.722 230.552L319.5 263.626L351.297 263.845L351.519 230.77L319.722 230.552ZM321.095 26.0947L320.873 59.1712L352.669 59.3895L352.891 26.313L321.095 26.0947ZM287.073 58.9391L287.295 25.8626L255.499 25.6443L255.277 58.7207L287.073 58.9391ZM255.27 59.7207L287.066 59.939L286.844 93.0146L255.048 92.7963L255.27 59.7207ZM254.274 58.7139L254.496 25.6374L222.702 25.4191L222.48 58.4955L254.274 58.7139ZM222.473 59.4955L254.267 59.7138L254.045 92.7894L222.251 92.5711L222.473 59.4955ZM221.477 58.4886L221.699 25.4122L189.903 25.1938L189.681 58.2703L221.477 58.4886ZM189.674 59.2703L221.47 59.4886L221.248 92.5642L189.452 92.3459L189.674 59.2703ZM188.678 58.2634L188.9 25.187L157.106 24.9686L156.884 58.0451L188.678 58.2634ZM156.877 59.0451L188.671 59.2634L188.449 92.339L156.655 92.1207L156.877 59.0451ZM155.881 58.0382L156.104 24.9618L124.307 24.7434L124.085 57.8199L155.881 58.0382ZM124.078 58.8198L155.875 59.0382L155.653 92.1138L123.856 91.8955L124.078 58.8198ZM123.082 57.813L123.304 24.7365L91.5098 24.5182L91.2877 57.5947L123.082 57.813ZM91.281 58.5946L123.075 58.813L122.853 91.8886L91.0589 91.6703L91.281 58.5946ZM90.2851 57.5878L90.5071 24.5113L58.7108 24.293L58.4888 57.3695L90.2851 57.5878ZM58.482 58.3694L90.2784 58.5878L90.0563 91.6634L58.26 91.445L58.482 58.3694ZM57.4861 57.3626L57.7082 24.2861L25.9138 24.0678L25.6918 57.1443L57.4861 57.3626ZM25.6851 58.1442L57.4794 58.3625L57.2574 91.4382L25.463 91.2198L25.6851 58.1442ZM222.244 93.5711L254.039 93.7894L253.817 126.866L222.022 126.647L222.244 93.5711ZM255.041 93.7963L254.819 126.873L286.615 127.091L286.837 94.0146L255.041 93.7963ZM222.015 127.647L253.81 127.866L253.588 160.942L221.793 160.724L222.015 127.647ZM254.812 127.872L254.59 160.949L286.386 161.167L286.609 128.091L254.812 127.872ZM221.786 161.724L253.581 161.942L253.359 195.018L221.564 194.799L221.786 161.724ZM254.584 161.949L254.362 195.025L286.158 195.243L286.38 162.167L254.584 161.949ZM221.558 195.799L253.352 196.018L253.13 229.095L221.336 228.876L221.558 195.799ZM254.355 196.024L254.133 229.102L285.929 229.32L286.151 196.243L254.355 196.024ZM221.329 229.876L253.124 230.095L252.901 263.169L221.107 262.951L221.329 229.876ZM254.126 230.102L253.904 263.176L285.7 263.394L285.922 230.32L254.126 230.102ZM221.019 126.64L221.241 93.5642L189.445 93.3458L189.223 126.422L221.019 126.64ZM189.216 127.422L221.013 127.64L220.791 160.717L188.994 160.499L189.216 127.422ZM188.22 126.415L188.442 93.339L156.649 93.1206L156.427 126.197L188.22 126.415ZM156.42 127.197L188.214 127.415L187.992 160.492L156.198 160.273L156.42 127.197ZM155.424 126.19L155.646 93.1138L123.849 92.8954L123.627 125.972L155.424 126.19ZM123.62 126.972L155.417 127.19L155.195 160.266L123.398 160.048L123.62 126.972ZM122.624 125.965L122.847 92.8885L91.0522 92.6702L90.8302 125.746L122.624 125.965ZM90.8234 126.746L122.618 126.965L122.396 160.041L90.6014 159.823L90.8234 126.746ZM89.8275 125.74L90.0496 92.6633L58.2533 92.445L58.0312 125.521L89.8275 125.74ZM58.0245 126.521L89.8208 126.74L89.5988 159.816L57.8024 159.598L58.0245 126.521ZM57.0286 125.514L57.2506 92.4381L25.4563 92.2198L25.2342 125.296L57.0286 125.514ZM25.2275 126.296L57.0219 126.514L56.7998 159.591L25.0055 159.372L25.2275 126.296ZM156.191 161.273L187.985 161.492L187.763 194.567L155.969 194.349L156.191 161.273ZM188.988 161.499L188.765 194.574L220.562 194.792L220.784 161.717L188.988 161.499ZM155.962 195.349L187.756 195.567L187.534 228.644L155.74 228.426L155.962 195.349ZM188.759 195.574L188.537 228.651L220.333 228.87L220.555 195.792L188.759 195.574ZM155.734 229.426L187.527 229.644L187.305 262.719L155.511 262.5L155.734 229.426ZM188.53 229.651L188.308 262.726L220.104 262.944L220.326 229.87L188.53 229.651ZM154.966 194.342L155.188 161.266L123.392 161.048L123.17 194.124L154.966 194.342ZM123.163 195.124L154.96 195.342L154.738 228.419L122.941 228.201L123.163 195.124ZM122.167 194.117L122.389 161.041L90.5947 160.823L90.3726 193.898L122.167 194.117ZM90.3659 194.898L122.16 195.117L121.938 228.194L90.1438 227.976L90.3659 194.898ZM89.37 193.892L89.592 160.816L57.7957 160.598L57.5737 193.673L89.37 193.892ZM57.567 194.673L89.3633 194.892L89.1412 227.969L57.3449 227.75L57.567 194.673ZM56.571 193.666L56.7931 160.591L24.9988 160.372L24.7767 193.448L56.571 193.666ZM24.77 194.448L56.5643 194.666L56.3423 227.743L24.5479 227.525L24.77 194.448ZM90.1371 228.976L121.931 229.194L121.709 262.268L89.9151 262.05L90.1371 228.976ZM122.934 229.201L122.712 262.275L154.509 262.494L154.731 229.419L122.934 229.201ZM24.3192 261.6L56.1135 261.818L56.3355 228.743L24.5412 228.525L24.3192 261.6ZM57.1161 261.825L88.9124 262.043L89.1345 228.969L57.3382 228.75L57.1161 261.825Z"
      />
      <defs>
        <radialGradient
          id="paint0_radial_502_11232"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(194.5 -111.5) rotate(90.2014) scale(284.502 238.209)"
        >
          <stop stop-color="white" />
          <stop offset="1" stop-color="white" stop-opacity="0" />
        </radialGradient>
      </defs>
    </svg>
  );
}

function SuccessIcon() {
  return (
    <svg
      class="self-center"
      xmlns="http://www.w3.org/2000/svg"
      width="94"
      height="94"
      viewBox="0 0 94 94"
      fill="none"
    >
      <g filter="url(#filter0_d_502_11260)">
        <path
          d="M70.5362 39.2057C69.714 38.1086 69.2573 36.92 69.0745 35.5486L68.3437 30.52C67.9783 27.8686 65.8772 25.7657 63.1365 25.3086L58.1121 24.5771C56.8331 24.3943 55.5541 23.8457 54.4579 23.1143L50.5297 20.1886C49.4334 19.3657 48.1545 19 46.8755 19C45.5966 19 44.3176 19.3657 43.2214 20.1886L39.2018 23.2057C38.1055 24.0286 36.9179 24.4857 35.5476 24.6686L30.6145 25.4C27.9652 25.7657 25.8641 27.8686 25.4073 30.6114L24.6765 35.64C24.4938 36.92 23.9457 38.2 23.2148 39.2971L20.2002 43.32C18.5558 45.5143 18.5558 48.5314 20.2002 50.6343L23.2148 54.6571C24.037 55.7543 24.4938 56.9429 24.6765 58.3143L25.4073 63.3429C25.7727 65.9943 27.8739 68.0971 30.6145 68.5543L35.639 69.2857C36.9179 69.4686 38.1969 70.0171 39.2931 70.7486L43.3127 73.7657C45.5052 75.4114 48.5199 75.4114 50.621 73.7657L54.6406 70.7486C55.7369 69.9257 56.9245 69.4686 58.2948 69.2857L63.3192 68.5543C65.9685 68.1886 68.0696 66.0857 68.5264 63.3429L69.2572 58.3143C69.44 57.0343 69.9881 55.7543 70.7189 54.6571L73.7336 50.6343C75.378 48.44 75.378 45.4229 73.7336 43.32L70.5362 39.2057Z"
          fill="#1CA960"
        />
        <path
          d="M70.0383 39.5789L70.0382 39.5789L70.0449 39.5875L73.2423 43.7018L73.2434 43.7033C74.7081 45.5763 74.7179 48.2832 73.2357 50.2611L70.221 54.284L70.2106 54.2978L70.2011 54.3122C69.4297 55.4701 68.8399 56.8363 68.6414 58.2255C68.6414 58.2258 68.6413 58.2261 68.6413 58.2264L67.9116 63.2468C67.495 65.7327 65.5972 67.6117 63.2341 67.9379L63.2296 67.9385L58.2125 68.669C58.2112 68.6691 58.21 68.6693 58.2087 68.6695C56.7436 68.8655 55.4548 69.3594 54.2671 70.2509L50.2475 73.2681L50.2474 73.268L50.2374 73.2759C48.3662 74.7414 45.6623 74.7513 43.6862 73.2681L39.6667 70.2509L39.6528 70.2406L39.6385 70.231C38.4815 69.4591 37.1165 68.8686 35.7283 68.6699C35.7279 68.6699 35.7275 68.6698 35.727 68.6698L30.7106 67.9395C28.2276 67.5227 26.3498 65.6237 26.0237 63.2579L26.0231 63.2534L25.2933 58.2321C25.2931 58.2309 25.2929 58.2296 25.2928 58.2283C25.0969 56.7623 24.6035 55.4726 23.7128 54.284L20.6981 50.2611L20.6982 50.2611L20.6903 50.251C19.2257 48.378 19.2158 45.6711 20.6981 43.6931L23.7128 39.6703L23.7231 39.6565L23.7327 39.6421C24.504 38.4841 25.0939 37.1179 25.2924 35.7287C25.2924 35.7285 25.2924 35.7282 25.2925 35.7279L26.0221 30.7075C26.4387 28.2216 28.3365 26.3426 30.6996 26.0164L30.6996 26.0164L30.7058 26.0155L35.6345 25.2847C37.0993 25.0886 38.3878 24.5947 39.5753 23.7033L43.5949 20.6862C44.572 19.9528 45.7163 19.6222 46.8755 19.6222C48.0348 19.6222 49.179 19.9528 50.1561 20.6862L50.158 20.6876L54.0862 23.6133L54.0992 23.6229L54.1126 23.6319C55.2697 24.4039 56.635 24.9944 58.0233 25.193C58.0235 25.193 58.0238 25.1931 58.024 25.1931L63.0403 25.9233C65.5234 26.3402 67.4013 28.2391 67.7273 30.605L67.728 30.6095L68.4578 35.6307C68.4579 35.632 68.4581 35.6333 68.4583 35.6346C68.6542 37.1006 69.1476 38.3903 70.0383 39.5789Z"
          stroke="white"
          stroke-width="1.24444"
          stroke-linecap="round"
        />
      </g>
      <path
        d="M57.0783 40.1556L43.869 53.4657C43.3679 53.9707 42.5553 53.9707 42.0541 53.4657L36.5449 47.9144"
        stroke="white"
        stroke-width="3.04197"
        stroke-linecap="round"
      />
      <defs>
        <filter
          id="filter0_d_502_11260"
          x="0.300129"
          y="0.333332"
          width="93.3333"
          height="93.3333"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="9.33333" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.0588235 0 0 0 0 0.533333 0 0 0 0 0.286275 0 0 0 0.25 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_502_11260"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_502_11260"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

function SavingsArrow() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="101"
      height="6"
      viewBox="0 0 101 6"
      fill="none"
    >
      <path
        opacity="0.3"
        d="M100.387 3L97.5 5.88675L94.6132 3L97.5 0.113249L100.387 3ZM97.5 3.5H0.5V2.5H97.5V3.5Z"
        fill="url(#paint0_linear_502_11257)"
      />
      <defs>
        <linearGradient
          id="paint0_linear_502_11257"
          x1="97.866"
          y1="6.49993"
          x2="49.8685"
          y2="4.89056"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#0F8849" />
          <stop offset="1" stop-color="#0F8849" stop-opacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function SavingsArrowRtl() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="101"
      height="6"
      viewBox="0 0 101 6"
      fill="none"
    >
      <path
        opacity="0.3"
        d="M0.613249 3L3.5 5.88675L6.38675 3L3.5 0.113249L0.613249 3ZM3.5 3.5H100.5V2.5H3.5V3.5Z"
        fill="url(#paint0_linear_502_11259)"
      />
      <defs>
        <linearGradient
          id="paint0_linear_502_11259"
          x1="3.13396"
          y1="6.49993"
          x2="51.1315"
          y2="4.89056"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#0F8849" />
          <stop offset="1" stop-color="#0F8849" stop-opacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function FullTnc(props: { resId: string }) {
  return (
    <ResourceLoader
      fetch={async () => {
        return getMarkdown(props.resId);
      }}
    >
      {(markdown) => (
        <div class="bottom-0 left-0 right-0 top-0 flex flex-col overflow-scroll bg-baseTertiaryLight p-5 pt-10 text-medium">
          <span class={"title-primary text-[20px] font-semibold"}>
            Terms and conditions
          </span>
          <Spacer height={20} />
          <SolidMarkdown children={markdown} class="body-small" />
        </div>
      )}
    </ResourceLoader>
  );
}

function HowToUse(props: { productId: string }) {
  return (
    <ResourceLoader
      fetch={async () => {
        return getHowToRedeemDetails(props.productId);
      }}
    >
      {(reponse) => <HowItWorksSteps steps={reponse.howToUseSteps} />}
    </ResourceLoader>
  );
}

function HowItWorksSteps(props: { steps: HowToUseSteps[] }) {
  const [steps, setSteps] = createSignal(props.steps[0]);

  return (
    <div class="box-border flex flex-col gap-2.5 rounded-[20px] rounded-b-none border border-baseTertiaryDark bg-baseTertiaryLight p-4 [box-shadow:0px_3px_8px_0px_#0000000a]">
      <span class="title-primary text-mediumSemiBold text-basePrimaryDark">
        How to use?
      </span>
      <div class="mb-1 flex flex-row gap-4 border-b border-baseTertiaryMedium text-[11px] font-bold uppercase text-baseSecondaryMedium ">
        <For each={props.steps}>
          {(step) => (
            <div
              class={classNames(
                "cursor-pointer pb-1",
                steps().retailMode == step.retailMode
                  ? "border-b-2 border-basePrimaryDark text-basePrimaryDark"
                  : ""
              )}
              onClick={() => setSteps(step)}
            >
              {step.retailModeName}
            </div>
          )}
        </For>
      </div>

      <For each={steps().howToUseHints.hints}>
        {(step, index) => (
          <div class="flex flex-row gap-2 text-medium text-textDark">
            <span class="flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-[18px]">
              {index() + 1}
            </span>
            <span class="body-small">{step}</span>
          </div>
        )}
      </For>
    </div>
  );
}
